/*
 * Description:
 * ---------------------
 * Outside Agents: the keys an AI agent outside Weavian, AWS DevOps Agent
 * first, uses to read a customer's network through the Agent Mesh's door for
 * them (server/pkg/mcpgw). A customer's administrators make a key for an
 * agent, for a time and for the questions they pick, see it this once with
 * the address and header to paste beside it, see each key's use and when it
 * stops working, and revoke one. What an agent asks shows on the
 * Investigations screen. A server that offers no choice of questions makes
 * keys that work until revoked, so the form asks only for the name there.
 * All wording is in meshWords.js.
 *
 * ! file components/OutsideAgentsPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCopy } from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useConfirm } from "./ConfirmModal";
import * as W from "./meshWords";
import "./Firewall.css";
import "./Mesh.css";

function Copyable({ label, value }) {
  const [done, setDone] = useState(false);
  const copy = () => navigator.clipboard?.writeText(value).then(() => {
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  });
  return (
    <div className="mesh-copy">
      <span className="fw-muted">{label}</span>
      <code>{value}</code>
      <button type="button" className="fw-btn" onClick={copy} aria-label={`Copy ${label}`}><FiCopy size={14} /> {done ? "Copied" : "Copy"}</button>
    </div>
  );
}

export default function OutsideAgentsPanel({ serverApiUrl }) {
  const confirm = useConfirm();
  const [keys, setKeys] = useState(null);
  const [domains, setDomains] = useState([]);
  const [customer, setCustomer] = useState("");
  const [name, setName] = useState("AWS DevOps Agent");
  const [offered, setOffered] = useState(null);
  const [days, setDays] = useState(W.WORKS_FOR_DEFAULT);
  const [picked, setPicked] = useState(null);
  const [made, setMade] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const send = useCallback(async (method, path, body) => {
    const res = await fetchWithTimeout(serverApiUrl(path), {
      method, credentials: "same-origin", headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    }, 15000).catch(() => null);
    const out = res ? await res.json().catch(() => ({})) : {};
    if (!res) throw new Error("The Weavian server did not answer.");
    if (res.status === 404 && path === "/mcp/keys") throw new Error("This Weavian server does not take outside agents yet.");
    if (!res.ok) throw new Error(out.error || `The server refused (HTTP ${res.status}).`);
    return out;
  }, [serverApiUrl]);

  const read = useCallback(() => send("GET", "/mcp/keys")
    .then((out) => {
      if (!mounted.current) return;
      setKeys(out.keys || []);
      setOffered(Array.isArray(out.tools) ? out.tools : null);
      setPicked((p) => p || (Array.isArray(out.tools) ? out.tools.map((t) => t.name) : null));
      setError("");
    })
    .catch((e) => mounted.current && setError(e.message)), [send]);
  useEffect(() => { read(); }, [read]);
  useEffect(() => {
    send("GET", "/domains").then((d) => {
      if (!mounted.current) return;
      setDomains(d.domains || []);
      setCustomer((c) => c || d.domains?.[0]?.id || "");
    }).catch(() => {});
  }, [send]);

  const toggle = (tool) => setPicked((p) => (p.includes(tool) ? p.filter((t) => t !== tool) : [...p, tool]));
  const make = async () => {
    setBusy(true);
    try {
      const grant = offered
        ? { expires: new Date(Date.now() + days * 864e5).toISOString(), tools: offered.map((t) => t.name).filter((n) => picked.includes(n)) }
        : {};
      const out = await send("POST", "/mcp/keys", { customer, name, ...grant });
      if (!mounted.current) return;
      // The server names its own path; the console reaches the server's API
      // root at serverApiUrl, which is the address an agent outside uses too.
      setMade({ ...out, url: new URL(serverApiUrl("/mcp"), window.location.href).href });
      setError("");
      read();
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const revoke = async (k) => {
    if (!(await confirm({ ...W.askRevoke(k), variant: "danger" }))) return;
    try {
      await send("DELETE", `/mcp/keys/${encodeURIComponent(k.id)}`);
      if (made?.key?.id === k.id) setMade(null);
      read();
    } catch (e) {
      setError(e.message);
    }
  };

  const names = Object.fromEntries(domains.map((d) => [d.id, d.name || d.id]));
  return (
    <div className="fw">
      {error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>}
      <section className="fw-card">
        <p className="fw-lead">{W.AGENTS_LEAD}</p>
        <div className="mesh-ask">
          {domains.length > 1 && (
            <label>For{" "}
              <select className="fw-input" value={customer} onChange={(e) => setCustomer(e.target.value)} aria-label="Customer">
                {domains.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>)}
              </select>
            </label>
          )}
          <label className="mesh-grow">{W.AGENT_NAME}{" "}
            <input className="fw-input" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} title={W.AGENT_NAME_HINT} />
          </label>
          {offered && (
            <label>{W.WORKS_FOR}{" "}
              <select className="fw-input" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={W.WORKS_FOR}>
                {W.WORKS_FOR_CHOICES.map((c) => <option key={c.days} value={c.days}>{c.label}</option>)}
              </select>
            </label>
          )}
          <button type="button" className="fw-btn is-primary" disabled={busy || !customer || !name.trim() || picked?.length === 0} onClick={make}>{W.MAKE_KEY}</button>
        </div>
        {offered && (
          <fieldset className="mesh-tools">
            <legend>{W.MAY_ASK}</legend>
            {offered.map((t) => (
              <label key={t.name}>
                <input type="checkbox" checked={picked.includes(t.name)} onChange={() => toggle(t.name)} /> {t.title}
              </label>
            ))}
            {picked.length === 0 && <span className="fw-muted">{W.PICK_A_QUESTION}</span>}
          </fieldset>
        )}
        {made && (
          <div className="mesh-made">
            <p className="fw-lead">{W.KEY_SHOWN_ONCE}</p>
            <p>{W.madeGrant(made.key, offered)}</p>
            <Copyable label="Server address (Streamable HTTP)" value={made.url} />
            <Copyable label="Header" value={made.header} />
            <Copyable label="Key" value={made.token} />
          </div>
        )}
      </section>
      <section className="fw-card">
        {keys === null && !error && <p className="fw-muted">Reading…</p>}
        {keys?.length === 0 && <p className="fw-muted">{W.NO_KEYS}</p>}
        {keys?.length > 0 && (
          <div className="fw-log-wrap">
            <table className="fw-log cards-on-phone">
              <thead><tr>{Object.values(W.KEY_COLS).map((c) => <th key={c}>{c}</th>)}<th aria-label="Actions" /></tr></thead>
              <tbody>
                {keys.map((k) => {
                  const ends = W.keyEnds(k);
                  return (
                    <tr key={k.id}>
                      <td><strong>{k.name}</strong></td>
                      <td data-label={W.KEY_COLS.customer}>{names[k.customer] || k.customer}</td>
                      <td data-label={W.KEY_COLS.made}>{k.by}, {W.when(k.created)}</td>
                      <td data-label={W.KEY_COLS.works}>{ends.tone ? <span className={`mesh-answer is-${ends.tone}`}><span className="mesh-dot" aria-hidden="true" /> {ends.text}</span> : ends.text}</td>
                      <td data-label={W.KEY_COLS.ask}>{W.keyTools(k, offered)}</td>
                      <td data-label={W.KEY_COLS.use}>{W.keyUse(k)}</td>
                      <td><button type="button" className="fw-btn is-danger-quiet" onClick={() => revoke(k)}>Revoke</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
